import {
    ActionIcon,
    TextInput,
    Textarea,
    Stack,
    Group,
    Title,
    Select,
    Divider,
    Alert,
} from "@mantine/core";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { RichTextEditor, Link } from "@mantine/tiptap";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useSettingsFormContext } from "./SettingsForm";
import sanitizeText from "../../utils/sanitizeText";

const durationOptions = Array.from({ length: 30 }, (v, k) =>
    k === 0
        ? { value: `${k + 1}`, label: `${k + 1} day` }
        : { value: `${k + 1}`, label: `${k + 1} days` }
);

const RTEComponent = ({ position }: { position: number }) => {
    const form = useSettingsFormContext();

    const editor = useEditor({
        extensions: [StarterKit, Underline, Link],
        onUpdate(props) {
            const content = props.editor.getHTML();
            form.setFieldValue(
                `configurations.${position}.email_template_model.message`,
                sanitizeText(content)
            );
        },
        content:
            form.values.configurations?.[position]?.email_template_model
                ?.message || "",
    });

    return (
        <RichTextEditor
            editor={editor}
            styles={{
                content: {
                    minHeight: "200px",
                    "& .ProseMirror": {
                        minHeight: "180px",
                    },
                },
            }}
        >
            <RichTextEditor.Toolbar sticky stickyOffset={60}>
                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>

                <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Link />
                    <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>
            </RichTextEditor.Toolbar>

            <RichTextEditor.Content />
        </RichTextEditor>
    );
};

interface Props {
    handleDelete: () => void;
    position: number;
}

const ReminderConfigItem = (props: Props) => {
    const { handleDelete, position } = props;

    const form = useSettingsFormContext();

    const durationLabel = () => {
        if (position === 0) {
            return "-";
        }
        if (position === 1) {
            return "Invitation";
        }
        return `Reminder ${position - 1}`;
    };

    return (
        <Stack spacing="md" mb="md">
            {position !== 0 && <Divider my="xs" variant="dashed" />}
            <Group position="apart" align="center">
                <Title order={6} size="h4">
                    {position === 0 ? "Invitation" : `Reminder ${position}`}
                </Title>
                <ActionIcon
                    onClick={handleDelete}
                    disabled={
                        form.values.isCommunicationsEnabled && position === 0
                    }
                >
                    <IconTrash />
                </ActionIcon>
            </Group>
            {position === 0 && (
                <Alert
                    variant="light"
                    color="yellow"
                    title=""
                    icon={<IconInfoCircle />}
                >
                    Invitation is sent immediately upon referral.
                </Alert>
            )}

            {position !== 0 && (
                <Select
                    w={250}
                    label={`Days elapsed since: ${durationLabel()}`}
                    placeholder="Select duration"
                    data={durationOptions}
                    radius="md"
                    withAsterisk
                    {...form.getInputProps(
                        `configurations.${position}.schedule_days`
                    )}
                    value={
                        form.values.configurations?.[
                            position
                        ]?.schedule_days.toString() || ""
                    }
                    data-qa="customerSettings_scheduleField"
                />
            )}

            <Textarea
                label="SMS Message Template"
                minRows={6}
                rows={6}
                autosize
                withAsterisk
                {...form.getInputProps(
                    `configurations.${position}.sms_message`
                )}
                data-qa="customerSettings_smsMessageField"
            />
            <Group sx={{ alignItems: "flex-start" }}>
                <TextInput
                    w={200}
                    label="Email Subject"
                    placeholder="Email subject"
                    radius="md"
                    withAsterisk
                    {...form.getInputProps(
                        `configurations.${position}.email_template_model.subject`
                    )}
                    data-qa="customerSettings_emailSubjectField"
                />
                <TextInput
                    w={200}
                    label="Email Heading"
                    placeholder="Email Heading"
                    withAsterisk
                    {...form.getInputProps(
                        `configurations.${position}.email_template_model.heading`
                    )}
                    data-qa="customerSettings_emailHeadingField"
                />
                <TextInput
                    w={200}
                    label="CTA Text"
                    placeholder="CTA Text"
                    withAsterisk
                    {...form.getInputProps(
                        `configurations.${position}.email_template_model.cta`
                    )}
                    data-qa="customerSettings_emailCtaField"
                />
                <TextInput
                    w={200}
                    label="Template Alias"
                    placeholder="Template Alias"
                    withAsterisk
                    {...form.getInputProps(
                        `configurations.${position}.email_template`
                    )}
                    data-qa="customerSettings_emailTemplateField"
                />
            </Group>

            <TextInput
                label="Email Message"
                placeholder="Enter email message here if applicable"
                name="message"
                withAsterisk
                inputContainer={() => <RTEComponent position={position} />}
                {...form.getInputProps(
                    `configurations.${position}.email_template_model.message`
                )}
                data-qa="customerSettings_messageContentField"
            />
        </Stack>
    );
};

export default ReminderConfigItem;
